import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Drawer,
  Divider,
  Chip,
  CircularProgress,
  Button,
  Avatar,
  TextField,
  Tooltip,
  Alert,
  AlertTitle,
} from '@mui/material';

// Icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';

const ContactMessages = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedEmail, setEditedEmail] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/admin/public/g/c/l/a/contact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(res.data.result || []);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    if (user) fetchContacts();
  }, [user, fetchContacts]);

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await axios.post(
        `${apiUrl}/admin/public/u/s/c/contact/${id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Statut mis à jour');
      fetchContacts();
      setOpenDrawer(false);
    } catch (err) {
      toast.error('Erreur statut');
    }
  };

  const handleUpdateEmail = async () => {
    if (!editedEmail) return;
    try {
      await axios.post(
        `${apiUrl}/admin/public/u/e/c/contact/${selectedContact.id}`,
        { email: editedEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Email mis à jour');
      setIsEditingEmail(false);
      setContacts((prev) =>
        prev.map((c) =>
          c.id === selectedContact.id ? { ...c, email: editedEmail } : c
        )
      );
      setSelectedContact((prev) => ({ ...prev, email: editedEmail }));
    } catch (err) {
      toast.error('Erreur update email');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await axios.delete(`${apiUrl}/admin/public/d/c/contact/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Supprimé');
      setContacts(contacts.filter((c) => c.id !== id));
      setOpenDrawer(false);
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  const filteredContacts = useMemo(() => {
    return contacts.filter(
      (c) =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [contacts, searchTerm]);

  return (
    <Box sx={{ p: 3, pt: { xs: 10, md: 3 } }}>
      <Typography
        variant="h4"
        sx={{ fontWeight: 800, color: '#2B3674', mb: 2 }}
      >
        Messages Contact
      </Typography>

      {/* TEXTE INFORMATIF EN HAUT */}
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="inherit" />}
        sx={{
          mb: 4,
          borderRadius: '15px',
          bgcolor: '#F4F7FE',
          color: '#2B3674',
          border: '1px solid #E9EDF7',
          '& .MuiAlert-icon': { color: '#4318FF' },
        }}
      >
        <AlertTitle sx={{ fontWeight: 800 }}>Gestion du Workflow</AlertTitle>
        Suivez le traitement des messages : <strong>Nouveau</strong> (non lu),{' '}
        <strong>Lu</strong> (en cours de traitement), et{' '}
        <strong>Répondu</strong> (archivé). Vous pouvez modifier l'email d'un
        contact directement dans les détails si une erreur est détectée.
      </Alert>

      <Paper
        sx={{
          borderRadius: '20px',
          border: '1px solid #F4F7FE',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#F4F7FE' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#A3AED0' }}>
                  EXPÉDITEUR
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#A3AED0' }}>
                  MESSAGE
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#A3AED0' }}>
                  STATUT
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#A3AED0' }}>
                  SERVICES
                </TableCell>

                <TableCell
                  align="right"
                  sx={{ fontWeight: 700, color: '#A3AED0' }}
                >
                  ACTIONS
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress sx={{ m: 3 }} />
                  </TableCell>
                </TableRow>
              ) : (
                filteredContacts.map((contact) => (
                  <TableRow key={contact.id} hover>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Typography sx={{ fontWeight: 700, color: '#2B3674' }}>
                        {contact.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#A3AED0' }}>
                        {contact.email}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 250 }}>
                      <Tooltip title={contact.content}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#2B3674',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {contact.content}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={
                          contact.status === 'pending'
                            ? 'Nouveau'
                            : contact.status === 'read'
                              ? 'Lu'
                              : 'Répondu'
                        }
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor:
                            contact.status === 'pending'
                              ? '#E6F9F0'
                              : contact.status === 'read'
                                ? '#F4F7FE'
                                : '#E2E8F0',
                          color:
                            contact.status === 'pending'
                              ? '#05CD99'
                              : contact.status === 'read'
                                ? '#4318FF'
                                : '#2B3674',
                        }}
                      />
                    </TableCell>
                   <TableCell>
  <Chip
    label={contact.service_type}
    size="small"
    sx={{
      fontWeight: 600,
      // Logique directe : Couleur différente selon le type de service
      bgcolor: contact.service_type === 'Service client' ? '#E6F2FF' : '#FFF3E6',
      color: contact.service_type === 'Service client' ? '#2196F3' : '#FF9800',
      borderRadius: '8px'
    }}
  />
</TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <IconButton
                          onClick={() => {
                            setSelectedContact(contact);
                            setOpenDrawer(true);
                          }}
                          sx={{ color: '#4318FF', bgcolor: '#F4F7FE' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(contact.id)}
                          sx={{ color: '#EE5D50', bgcolor: '#FFF5F5' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>

                    
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => {
          setOpenDrawer(false);
          setIsEditingEmail(false);
        }}
        sx={{ zIndex: 3000 }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, p: 4 } }}
      >
        <Box
          sx={{
            width: { xs: '100vw', sm: 450 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              p: 3,
              bgcolor: '#F4F7FE',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#2B3674' }}>
              Détails du Message
            </Typography>
            <IconButton onClick={() => setOpenDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 4, flexGrow: 1, overflowY: 'auto' }}>
            {selectedContact && (
              <Stack spacing={4}>
                <Box>
                  <Typography
                    variant="overline"
                    sx={{ color: '#A3AED0', fontWeight: 800 }}
                  >
                    Informations
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: '#4318FF' }}>
                        <PersonIcon />
                      </Avatar>
                      <Typography sx={{ fontWeight: 700, color: '#2B3674' }}>
                        {selectedContact.name}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <EmailIcon sx={{ color: '#A3AED0' }} />
                      {isEditingEmail ? (
                        <TextField
                          size="small"
                          fullWidth
                          autoFocus
                          value={editedEmail}
                          onChange={(e) => setEditedEmail(e.target.value)}
                        />
                      ) : (
                        <Typography sx={{ fontWeight: 600, color: '#4318FF' }}>
                          {selectedContact.email}
                        </Typography>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (isEditingEmail) handleUpdateEmail();
                          else {
                            setEditedEmail(selectedContact.email);
                            setIsEditingEmail(true);
                          }
                        }}
                      >
                        {isEditingEmail ? (
                          <SaveIcon color="primary" />
                        ) : (
                          <EditIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <PhoneIcon sx={{ color: '#A3AED0' }} />
                      <Typography sx={{ color: '#2B3674' }}>
                        {selectedContact.tel || 'N/A'}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                <Box>
                  <Typography
                    variant="overline"
                    sx={{ color: '#A3AED0', fontWeight: 800 }}
                  >
                    Message Complet
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      mt: 1,
                      bgcolor: '#F4F7FE',
                      borderRadius: '15px',
                      border: '1px solid #E9EDF7',
                    }}
                    elevation={0}
                  >
                    <Typography
                      sx={{
                        whiteSpace: 'pre-line',
                        color: '#2B3674',
                        fontSize: '15px',
                        lineHeight: 1.6,
                      }}
                    >
                      {selectedContact.content}
                    </Typography>
                  </Paper>
                </Box>

                <Divider />

                <Stack spacing={2}>
                  {selectedContact.status === 'pending' && (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() =>
                        handleUpdateStatus(selectedContact.id, 'read')
                      }
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 700,
                      }}
                    >
                      Marquer comme lu
                    </Button>
                  )}
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<EmailIcon />}
                    onClick={() =>
                      handleUpdateStatus(selectedContact.id, 'completed')
                    }
                    sx={{
                      bgcolor: '#05CD99',
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': { bgcolor: '#04b386' },
                    }}
                  >
                    {selectedContact.status === 'completed'
                      ? 'Déjà traité'
                      : 'Confirmer la réponse mail'}
                  </Button>
                  <Button
                    fullWidth
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(selectedContact.id)}
                    sx={{ textTransform: 'none' }}
                  >
                    Supprimer définitivement
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default ContactMessages;
